import { getHeaders } from "./api";
import { NoteWorkflowsDocument } from "../components/NoteWorkflows/noteWorkflowsTypes";
import { normalizeDocument } from "../components/NoteWorkflows/noteWorkflowsMutations";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

export class NoteWorkflowsConflictError extends Error {
    document: NoteWorkflowsDocument;

    constructor(message: string, document: NoteWorkflowsDocument) {
        super(message);
        this.name = "NoteWorkflowsConflictError";
        this.document = document;
    }
}

export async function getCompanyNoteWorkflowsApi(
    idToken: string | undefined
): Promise<NoteWorkflowsDocument> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/note-workflows`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return normalizeDocument(data.note_workflows);
}

const WORKFLOW_PRESENCE_TIMEOUT_MS = 5000;
let workflowPresencePromise: Promise<boolean> | null = null;

/**
 * Whether this company configured at least one Note Workflow. Memoized for the page load like
 * configApi, because every Document tip mount asks the same question.
 *
 * A timeout or an error resolves to false: the legacy note flow needs no configuration, so the
 * employee can always document. Waiting forever cannot fall back to anything.
 */
export async function hasCompanyNoteWorkflowsApi(idToken: string | undefined): Promise<boolean> {
    if (workflowPresencePromise) {
        return workflowPresencePromise;
    }
    workflowPresencePromise = Promise.race([
        getCompanyNoteWorkflowsApi(idToken).then(document => document.workflows.length > 0),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), WORKFLOW_PRESENCE_TIMEOUT_MS))
    ]).catch(error => {
        // Drop the memo so the next mount retries instead of caching a transient failure.
        workflowPresencePromise = null;
        console.error("Failed to read company note workflows", error);
        return false;
    });
    return workflowPresencePromise;
}

export async function updateCompanyNoteWorkflowsApi(
    payload: NoteWorkflowsDocument,
    idToken: string | undefined
): Promise<NoteWorkflowsDocument> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/note-workflows`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });
    if (response.status === 409) {
        let message = "Note workflows were updated by someone else";
        let current: unknown = undefined;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
            current = data?.note_workflows;
        } catch {
            /* fall through */
        }
        throw new NoteWorkflowsConflictError(message, normalizeDocument(current));
    }
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            /* fall through */
        }
        throw new Error(message);
    }
    const data = await response.json();
    return normalizeDocument(data.note_workflows);
}
